/**
 * What somebody has typed into a pre-consultation form, kept on their own
 * device until they send it.
 *
 * The form is nine screens against a single-use token and every answer lives
 * in one page's state — so a phone that reclaimed the tab, a back gesture, or
 * a browser killed while the reader went to find their pincode took the lot
 * and put them back on the greeting. This is the whole of the fix, and it is
 * deliberately the smallest one: nothing here reaches the network. There is no
 * draft endpoint, the submit stays the one write this feature makes, and a
 * form half-filled is the reader's own business until they press Submit.
 *
 * `localStorage` rather than `sessionStorage`, which would die with the tab —
 * and the tab dying is the case people actually lose answers to. That buys the
 * two rules below, and both are load-bearing rather than tidiness:
 *
 *   **Keyed by the link.** A form is opened from a message on a phone two
 *   people share. A draft under a bare key would open somebody else's answers
 *   — their weight, their medications — in front of the next reader, so the
 *   key carries the practitioner's slug and the token, and a link that is not
 *   yours matches nothing.
 *
 *   **Cleared, and swept.** Gone the moment the answers are sent or the link
 *   turns out to be spent, and anything left behind by a form somebody
 *   abandoned is dropped once it is `MAX_AGE_MS` old. These are health details
 *   and they should not sit on a device forever.
 *
 * Storage can throw outright — Safari's private mode, a browser with site data
 * blocked — so every access is guarded and a failure means no autosave rather
 * than no form. The reader loses nothing they had before this existed.
 */

import { PRECONSULTATION_STEPS } from "./preconsultationSteps";
import type { PreconsultationStepId } from "./preconsultationSteps";
import { EMPTY_ANSWERS } from "./preconsultationValidation";
import type { PreconsultationAnswers } from "./types/preconsultation.types";

/** One key per link — see the note above on why the token is in it. */
const KEY_PREFIX = "naurist:preconsult-draft:";

/**
 * Bumped whenever `PreconsultationAnswers` changes shape. A draft written by
 * an older build is dropped rather than restored: the steps that would draw it
 * have moved on, and half a shape read into a controlled input is a field that
 * silently stops working.
 *
 * 2 — the one favourite cuisine became a list of them, and the allergy
 *     question came back onto the food step.
 * 3 — the one medical condition became a list of them.
 */
const VERSION = 3;

/**
 * How long an untouched draft is kept.
 *
 * Longer than any link this backend mints and short enough that a form
 * abandoned once does not follow somebody around: past this the token has
 * almost certainly expired, so what is being kept is health answers that can
 * no longer be sent anywhere.
 */
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/**
 * How long the typing has to stop before a draft is written.
 *
 * `answers` changes on every keystroke and a write is a `JSON.stringify` and a
 * synchronous store on the main thread. A step change writes at once — see the
 * page — so this only ever delays the last few characters of a box, which the
 * flush on hide catches anyway.
 */
export const DRAFT_SAVE_MS = 500;

/** Where a reader had got to, and what they had put in. */
export interface PreconsultationDraft {
  answers: PreconsultationAnswers;
  /** The step they were on. Where the form reopens, if it is still asked. */
  step: PreconsultationStepId;
}

/** The draft as it sits in storage, with the two fields only this file reads. */
interface StoredDraft extends PreconsultationDraft {
  version: number;
  /** Epoch milliseconds. What the sweep below measures age against. */
  savedAt: number;
}

const keyFor = (handle: string, token: string): string =>
  `${KEY_PREFIX}${handle}:${token}`;

/**
 * The stored answers, field by field, over the empty ones.
 *
 * Nothing is trusted whole: a draft written before a field existed is missing
 * it, and one written after a field was removed carries a key nothing reads.
 * So the empty answers are the shape, and a stored value is taken only where
 * it is the same kind of thing — a string for a string, entries for a list.
 * The alternative is `undefined` reaching a controlled input, which turns a
 * box uncontrolled for the rest of the sitting.
 *
 * Not defended against beyond that. Somebody who hand-edits their own draft to
 * put a nonsense unit in their own record has done exactly that and no more.
 */
const answersOf = (raw: unknown): PreconsultationAnswers => {
  const held = (raw ?? {}) as Record<string, unknown>;
  const answers: Record<string, unknown> = { ...EMPTY_ANSWERS };

  Object.entries(EMPTY_ANSWERS).forEach(([field, empty]) => {
    const value = held[field];

    if (Array.isArray(empty)) {
      if (Array.isArray(value)) {
        answers[field] = value.filter((entry) => typeof entry === "string");
      }
      return;
    }

    if (typeof value === typeof empty) answers[field] = value;
  });

  return answers as unknown as PreconsultationAnswers;
};

/**
 * The step it was left on, or the first one.
 *
 * A step this build has never heard of falls back rather than throwing the
 * answers away — the reader keeps everything they typed and walks forward
 * through it again, which is far better than a form that opens empty.
 */
const stepOf = (raw: unknown): PreconsultationStepId =>
  PRECONSULTATION_STEPS.find((step) => step.id === raw)?.id ?? "basics";

/**
 * Drops every draft past `MAX_AGE_MS`, this link's or anybody's.
 *
 * Once per load, on the first write. A reader only ever reads their own key,
 * so without this a form abandoned on a shared phone would sit there until the
 * browser was cleared — the sweep is what makes the age limit mean anything.
 */
let swept = false;

const sweep = (): void => {
  swept = true;

  try {
    const stale: string[] = [];

    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(KEY_PREFIX)) continue;

      const raw = window.localStorage.getItem(key);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      const savedAt = (parsed as StoredDraft | null)?.savedAt;

      // Anything unreadable goes too: it cannot be restored, so it is bytes
      // sitting on somebody's phone for no reason.
      if (typeof savedAt !== "number" || Date.now() - savedAt > MAX_AGE_MS) {
        stale.push(key);
      }
    }

    // Collected first, then removed: the loop above indexes into the very list
    // a removal reshuffles.
    stale.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    /* unreadable storage sweeps itself — nothing is being written either */
  }
};

/**
 * What was left in this link's form, or nothing.
 *
 * `undefined` covers every reason there is no draft to open on, and they are
 * one answer on purpose: none written, one from an older build, one past its
 * age, storage that will not answer. All of them mean the same thing to the
 * page — start at the greeting.
 */
export const readPreconsultationDraft = (
  handle: string,
  token: string
): PreconsultationDraft | undefined => {
  if (!handle.trim() || !token.trim()) return undefined;

  try {
    const raw = window.localStorage.getItem(keyFor(handle, token));
    if (!raw) return undefined;

    const stored = JSON.parse(raw) as StoredDraft | null;
    if (!stored || stored.version !== VERSION) return undefined;
    if (
      typeof stored.savedAt !== "number" ||
      Date.now() - stored.savedAt > MAX_AGE_MS
    ) {
      return undefined;
    }

    return { answers: answersOf(stored.answers), step: stepOf(stored.step) };
  } catch {
    return undefined;
  }
};

/** Keeps what has been typed so far. Silent where storage will not have it. */
export const writePreconsultationDraft = (
  handle: string,
  token: string,
  draft: PreconsultationDraft
): void => {
  if (!handle.trim() || !token.trim()) return;
  if (!swept) sweep();

  const stored: StoredDraft = { ...draft, version: VERSION, savedAt: Date.now() };

  try {
    window.localStorage.setItem(keyFor(handle, token), JSON.stringify(stored));
  } catch {
    /* full, or blocked. The form still works; it just will not survive. */
  }
};

/**
 * Forgets this link's draft.
 *
 * Called the moment the answers are sent, and the moment the link turns out to
 * be spent or expired — both are ends after which there is nothing left to
 * restore, and keeping health answers past them would be keeping them for
 * nobody.
 */
export const clearPreconsultationDraft = (
  handle: string,
  token: string
): void => {
  if (!handle.trim() || !token.trim()) return;

  try {
    window.localStorage.removeItem(keyFor(handle, token));
  } catch {
    /* nothing to undo */
  }
};
